import styled, { css } from 'styled-components';

// 기본 버튼 스타일
const baseButtonStyles = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  border: none;
  border-radius: ${props => props.theme.borderRadius.md};
  font-family: ${props => props.theme.typography.fontFamily};
  font-size: ${props => props.theme.typography.button.fontSize};
  font-weight: ${props => props.theme.typography.button.fontWeight};
  line-height: ${props => props.theme.typography.button.lineHeight};
  text-transform: ${props => props.theme.typography.button.textTransform};
  letter-spacing: ${props => props.theme.typography.button.letterSpacing};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.duration.standard}ms ${props => props.theme.transitions.easing.easeInOut};
  text-decoration: none;
  min-height: 44px;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    pointer-events: none;
  }
  
  &:focus {
    outline: 2px solid ${props => props.theme.colors.primary.main};
    outline-offset: 2px;
  }
`;

// Primary Button
export const PrimaryButton = styled.button`
  ${baseButtonStyles}
  background: linear-gradient(135deg, ${props => props.theme.colors.primary.main} 0%, ${props => props.theme.colors.primary.dark} 100%);
  color: ${props => props.theme.colors.primary.contrastText};
  box-shadow: ${props => props.theme.shadows.sm};
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, ${props => props.theme.colors.primary.dark} 0%, ${props => props.theme.colors.primary.main} 100%);
    box-shadow: ${props => props.theme.shadows.md};
    transform: translateY(-1px);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: ${props => props.theme.shadows.sm};
  }
`;

// Secondary Button
export const SecondaryButton = styled.button`
  ${baseButtonStyles}
  background: transparent;
  color: ${props => props.theme.colors.primary.main};
  border: 2px solid ${props => props.theme.colors.primary.main};
  
  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.primary.main};
    color: ${props => props.theme.colors.primary.contrastText};
    transform: translateY(-1px);
    box-shadow: ${props => props.theme.shadows.sm};
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: none;
  }
`;

// Danger Button
export const DangerButton = styled.button`
  ${baseButtonStyles}
  background: linear-gradient(135deg, ${props => props.theme.colors.error.main} 0%, ${props => props.theme.colors.error.dark} 100%);
  color: ${props => props.theme.colors.error.contrastText};
  box-shadow: ${props => props.theme.shadows.sm};
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, ${props => props.theme.colors.error.dark} 0%, ${props => props.theme.colors.error.main} 100%);
    box-shadow: ${props => props.theme.shadows.md};
    transform: translateY(-1px);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: ${props => props.theme.shadows.sm};
  }
`;

// Card Components
export const StyledCard = styled.div`
  background: ${props => props.theme.colors.background.paper};
  border-radius: ${props => props.theme.borderRadius.lg};
  box-shadow: ${props => props.theme.shadows.sm};
  border: 1px solid ${props => props.theme.colors.divider};
  overflow: hidden;
  transition: all ${props => props.theme.transitions.duration.standard}ms ${props => props.theme.transitions.easing.easeInOut};
  
  &:hover {
    box-shadow: ${props => props.theme.shadows.md};
    transform: translateY(-2px);
  }
`;

export const HoverCard = styled(StyledCard)`
  cursor: pointer;
  
  &:hover {
    box-shadow: ${props => props.theme.shadows.lg};
    transform: translateY(-4px);
  }
`;

// Typography Components
export const PageTitle = styled.h1`
  font-size: ${props => props.theme.typography.h1.fontSize};
  font-weight: ${props => props.theme.typography.h1.fontWeight};
  line-height: ${props => props.theme.typography.h1.lineHeight};
  letter-spacing: ${props => props.theme.typography.h1.letterSpacing};
  color: ${props => props.theme.colors.text.primary};
  margin: 0 0 ${props => props.theme.spacing.lg} 0;
`;

export const SectionTitle = styled.h2`
  font-size: ${props => props.theme.typography.h2.fontSize};
  font-weight: ${props => props.theme.typography.h2.fontWeight};
  line-height: ${props => props.theme.typography.h2.lineHeight};
  letter-spacing: ${props => props.theme.typography.h2.letterSpacing};
  color: ${props => props.theme.colors.text.primary};
  margin: 0 0 ${props => props.theme.spacing.md} 0;
`;

export const Subtitle = styled.h3`
  font-size: ${props => props.theme.typography.h3.fontSize};
  font-weight: ${props => props.theme.typography.h3.fontWeight};
  line-height: ${props => props.theme.typography.h3.lineHeight};
  letter-spacing: ${props => props.theme.typography.h3.letterSpacing};
  color: ${props => props.theme.colors.text.secondary};
  margin: 0 0 ${props => props.theme.spacing.sm} 0;
`;

// Layout Components
export const FlexBox = styled.div`
  display: flex;
  align-items: ${props => props.alignItems || 'center'};
  justify-content: ${props => props.justifyContent || 'flex-start'};
  flex-direction: ${props => props.direction || 'row'};
  gap: ${props => props.gap || props.theme.spacing.md};
  flex-wrap: ${props => props.wrap || 'nowrap'};
`;

export const CenteredBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: ${props => props.minHeight || 'auto'};
  width: ${props => props.width || '100%'};
`;

export const SpacedBox = styled.div`
  padding: ${props => props.padding || props.theme.spacing.md};
  margin: ${props => props.margin || '0'};
`;

// Paper Component
export const StyledPaper = styled.div`
  background: ${props => props.theme.colors.background.paper};
  border-radius: ${props => props.theme.borderRadius.md};
  box-shadow: ${props => props.theme.shadows.xs};
  border: 1px solid ${props => props.theme.colors.divider};
  padding: ${props => props.padding || props.theme.spacing.md};
`;

// Form Components
export const StyledTextField = styled.div`
  position: relative;
  margin-bottom: ${props => props.theme.spacing.md};
  
  label {
    display: block;
    font-size: ${props => props.theme.typography.body2.fontSize};
    font-weight: ${props => props.theme.typography.fontWeightMedium};
    color: ${props => props.theme.colors.text.primary};
    margin-bottom: ${props => props.theme.spacing.xs};
  }
  
  input, textarea, select {
    width: 100%;
    padding: 12px 16px;
    border: 2px solid ${props => props.theme.colors.grey[300]};
    border-radius: ${props => props.theme.borderRadius.md};
    font-family: ${props => props.theme.typography.fontFamily};
    font-size: ${props => props.theme.typography.body1.fontSize};
    line-height: ${props => props.theme.typography.body1.lineHeight};
    background: ${props => props.theme.colors.background.paper};
    color: ${props => props.theme.colors.text.primary};
    transition: all ${props => props.theme.transitions.duration.standard}ms ${props => props.theme.transitions.easing.easeInOut};
    
    &:focus {
      outline: none;
      border-color: ${props => props.theme.colors.primary.main};
      box-shadow: 0 0 0 3px ${props => props.theme.colors.primary.light}20;
    }
    
    &:disabled {
      background: ${props => props.theme.colors.grey[100]};
      color: ${props => props.theme.colors.text.disabled};
      cursor: not-allowed;
    }
    
    &::placeholder {
      color: ${props => props.theme.colors.text.disabled};
    }
  }
  
  &.error input {
    border-color: ${props => props.theme.colors.error.main};
    
    &:focus {
      box-shadow: 0 0 0 3px ${props => props.theme.colors.error.light}20;
    }
  }
  
  .error-message {
    color: ${props => props.theme.colors.error.main};
    font-size: ${props => props.theme.typography.caption.fontSize};
    margin-top: ${props => props.theme.spacing.xs};
  }
`;

// Status Components
export const StatusChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: ${props => props.theme.borderRadius.round};
  font-size: ${props => props.theme.typography.caption.fontSize};
  font-weight: ${props => props.theme.typography.fontWeightMedium};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  ${props => {
    switch (props.variant) {
      case 'success':
        return css`
          background: ${props.theme.colors.success.light}20;
          color: ${props.theme.colors.success.dark};
        `;
      case 'error':
        return css`
          background: ${props.theme.colors.error.light}20;
          color: ${props.theme.colors.error.dark};
        `;
      case 'warning':
        return css`
          background: ${props.theme.colors.warning.light}20;
          color: ${props.theme.colors.warning.dark};
        `;
      case 'info':
        return css`
          background: ${props.theme.colors.info.light}20;
          color: ${props.theme.colors.info.dark};
        `;
      default:
        return css`
          background: ${props.theme.colors.grey[200]};
          color: ${props.theme.colors.grey[700]};
        `;
    }
  }}
`;

// Loading Component
export const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid ${props => props.theme.colors.grey[300]};
  border-radius: 50%;
  border-top-color: ${props => props.theme.colors.primary.main};
  animation: spin 1s ease-in-out infinite;
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

// Divider Component
export const Divider = styled.hr`
  border: none;
  height: 1px;
  background: ${props => props.theme.colors.divider};
  margin: ${props => props.theme.spacing.md} 0;
`;

// Gradient Box
export const GradientBox = styled.div`
  background: ${props => props.gradient || `linear-gradient(135deg, ${props.theme.colors.primary.main} 0%, ${props.theme.colors.primary.dark} 100%)`};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.padding || props.theme.spacing.lg};
  color: ${props => props.theme.colors.primary.contrastText};
  box-shadow: ${props => props.theme.shadows.md};
`; 